import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth0 } from '@auth0/auth0-react';
import { Alert, Spin } from 'antd';
import styled from 'styled-components';

import { useAppSelector } from 'store/hook';

import { consumePostLoginRedirectPath } from './postLoginRedirect';
import { useAuth0TokenExchange } from './useAuth0TokenExchange';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f5f5f5;
`;

const StatusText = styled.p`
  margin-top: 20px;
  font-family: var(--font-regular);
  font-size: 16px;
  color: #666;
`;

const ReturnLink = styled.a`
  margin-top: 20px;
  color: #1f58b0;
  text-decoration: none;
  font-family: var(--font-regular);
  font-size: 14px;

  &:hover {
    text-decoration: underline;
  }
`;

/**
 * Auth0 callback handler component.
 *
 * This component is rendered at /auth/callback after Auth0 redirects back.
 * It handles:
 * 1. Waiting for Auth0 authentication to complete
 * 2. Exchanging the Auth0 token for PA tokens
 * 3. Redirecting to the appropriate page based on user role
 * 4. Displaying errors if authentication fails
 */
const Auth0Callback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading: auth0Loading, error: auth0Error } = useAuth0();
  const { exchangeToken, error: exchangeError } = useAuth0TokenExchange();
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const [hasExchanged, setHasExchanged] = useState(false);
  // Captured once on mount - it was set by the page that redirected here before Auth0 took over.
  const [redirectPath] = useState(() => consumePostLoginRedirectPath());

  // Handle Auth0 errors from URL params (e.g., user cancelled login)
  const urlError = searchParams.get('error');
  const urlErrorDescription = searchParams.get('error_description');

  useEffect(() => {
    // If there's an error in URL params, redirect to sign-in
    if (urlError) {
      navigate(`/sign-in?error=${encodeURIComponent(urlErrorDescription || urlError)}`);
      return;
    }

    // If Auth0 error occurred
    if (auth0Error) {
      navigate(`/sign-in?error=${encodeURIComponent(auth0Error.message)}`);
      return;
    }

    // Once Auth0 authentication is complete, exchange tokens
    if (isAuthenticated && !auth0Loading && !hasExchanged) {
      setHasExchanged(true);
      exchangeToken().catch((err) => {
        navigate(`/sign-in?error=${encodeURIComponent(err.message)}`);
      });
    }
  }, [isAuthenticated, auth0Loading, auth0Error, hasExchanged, exchangeToken, navigate, urlError, urlErrorDescription]);

  // Navigate after successful login (user is set in Redux)
  useEffect(() => {
    if (!currentUser) {
      return;
    }
    if (redirectPath) {
      navigate(redirectPath);
    } else if (currentUser.isSuperuser) {
      navigate('/companies');
    } else {
      navigate('/');
    }
  }, [currentUser, redirectPath, navigate]);

  // Show error if exchange failed
  if (exchangeError) {
    return (
      <Container>
        <Alert type="error" message="Authentication Failed" description={exchangeError} showIcon />
        <ReturnLink href="/sign-in">Return to Sign In</ReturnLink>
      </Container>
    );
  }

  return (
    <Container>
      <Spin size="large" />
      <StatusText>{auth0Loading || !isAuthenticated ? 'Authenticating...' : 'Completing login...'}</StatusText>
    </Container>
  );
};

export default Auth0Callback;
